import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RotateCcw } from "lucide-react";
import { startVision, visionStatus, previewStart, previewStatus } from "@/lib/photo";

const R = 92;
const C = 2 * Math.PI * R;
const CREEP = 8000; // montée jusqu'à 90 % en 8 s max (l'aperçu arrive souvent avant)
const MIN_MS = 1200; // durée minimale visible pour éviter un flash

const Loading = () => {
    const navigate = useNavigate();
    const [pct, setPct] = useState(1);
    const [lineIdx, setLineIdx] = useState(0);
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const status = useRef("pending"); // pending | done | face_error | error
    const finished = useRef(false);
    const autoRetries = useRef(0);
    const MAX_AUTO_RETRIES = 2;
    const isRescan = typeof window !== "undefined" && localStorage.getItem("revanche_rescan") === "1";
    const isPostpay = typeof window !== "undefined" && localStorage.getItem("revanche_postpay") === "1";
    const isFull = isRescan || isPostpay; // mode "vraie analyse complète" (après paiement ou rescan)

    const lines = React.useMemo(() => {
        const a = JSON.parse(localStorage.getItem("revanche_answers") || "{}");
        const taille = a.taille ?? "—";
        const poids = a.poids ?? "—";
        return [
            "Lecture des points du visage",
            "Analyse de la symétrie faciale",
            "Mesure de l'angle mandibulaire",
            "Évaluation de la structure osseuse",
            `Croisement avec ta taille de ${taille} cm`,
            `Croisement avec ton poids de ${poids} kg`,
            "Estimation de ta composition corporelle",
            "Analyse de la qualité de peau",
            "Lecture de ta posture",
            "Évaluation de ton style actuel",
            "Calcul de ton potentiel",
            "Rédaction de tes axes prioritaires",
        ];
    }, []);

    useEffect(() => {
        let cancelled = false;
        finished.current = false;
        status.current = "pending";
        autoRetries.current = 0;
        setFailed(false);
        setPct(1);
        const start = Date.now();
        const L = lines.length;

        const finishForward = () => {
            if (finished.current) return;
            finished.current = true;
            if (isRescan) {
                localStorage.removeItem("revanche_rescan");
                navigate("/accueil");
            } else if (isPostpay) {
                localStorage.removeItem("revanche_postpay");
                navigate("/apercu");
            } else {
                navigate("/apercu");
            }
        };

        // Après paiement / rescan : on attend la VRAIE analyse complète (IA).
        // Parcours normal (avant paiement) : aperçu modèle instantané, SANS aucun appel IA.
        const poll = async () => {
            if (cancelled) return;
            try {
                if (isFull) {
                    const s = await visionStatus();
                    if (cancelled) return;
                    if (s.status === "done") {
                        localStorage.setItem("revanche_analysis", JSON.stringify(s.analysis || {}));
                        status.current = "done";
                    } else if (s.status === "no_session") {
                        if (!finished.current) { finished.current = true; navigate("/"); }
                        return;
                    } else if (s.status === "error") {
                        // Le backend a déjà fait son unique relance automatique : on n'en ajoute pas d'autre.
                        status.current = "error"; setFailed(true); return;
                    } else if (s.status === "face_error") {
                        if (!finished.current) { finished.current = true; navigate("/photo", { state: { erreur: s.erreur_photo || "visage_non_detecte" } }); }
                        return;
                    } else if (s.status === "absent" || s.status === "locked") {
                        // Le job n'a pas encore démarré (déblocage en cours de propagation) : on le relance.
                        startVision().catch(() => {});
                    }
                } else {
                    const s = await previewStatus();
                    if (cancelled) return;
                    if (s.status === "done") {
                        const pv = s.preview || {};
                        localStorage.setItem("revanche_preview", JSON.stringify(pv));
                        localStorage.setItem("revanche_analysis", JSON.stringify(pv));
                        status.current = "done";
                    } else if (s.status === "no_session") {
                        if (!finished.current) { finished.current = true; navigate("/"); }
                        return;
                    } else if (s.status === "error") {
                        if (autoRetries.current < MAX_AUTO_RETRIES) {
                            autoRetries.current += 1; previewStart().catch(() => {});
                            if (!cancelled) setTimeout(poll, 1500); return;
                        }
                        status.current = "error"; setFailed(true); return;
                    } else if (s.status === "face_error") {
                        if (!finished.current) { finished.current = true; navigate("/photo", { state: { erreur: s.erreur_photo || "visage_non_detecte" } }); }
                        return;
                    }
                }
            } catch (e) { /* réseau : on retente */ }
            if (!cancelled && status.current === "pending") setTimeout(poll, 900);
        };

        const kick = async () => {
            // Session perdue (ex : cache vidé après retour de paiement) → on repart proprement,
            // sans afficher le message trompeur "on relance ton analyse".
            const sid = typeof window !== "undefined" ? localStorage.getItem("revanche_session_id") : null;
            if (!sid || sid === "null" || sid === "undefined") {
                if (!finished.current) { finished.current = true; navigate("/"); }
                return;
            }
            try {
                // Mode vraie analyse (après paiement/rescan) : on démarre l'IA (idempotent).
                // Mode normal (avant paiement) : aperçu modèle sans aucune IA.
                if (isFull) startVision().catch(() => {});
                else previewStart().catch(() => {});
            } catch (e) { /* noop */ }
            poll();
        };
        kick();

        const pctRef = { v: 1 };
        const tick = setInterval(() => {
            if (cancelled) return;
            const elapsed = Date.now() - start;
            const base = Math.min(90, Math.round((elapsed / CREEP) * 90));
            if (status.current === "done") {
                const next = Math.min(100, Math.round(pctRef.v) + 7);
                pctRef.v = next;
                setPct(next);
                if (next >= 100 && !finished.current && elapsed >= MIN_MS) setTimeout(finishForward, 0);
            } else if (status.current === "pending") {
                if (base < 90) pctRef.v = base;
                else pctRef.v = Math.min(98.9, pctRef.v + (99 - pctRef.v) * 0.02);
                setPct(Math.floor(pctRef.v));
            }
        }, 110);

        const txt = setInterval(() => {
            if (cancelled) return;
            const k = Math.floor((Date.now() - start) / (CREEP / L));
            setLineIdx(k < L ? k : L - 4 + ((k - L) % 4));
        }, 220);

        return () => { cancelled = true; clearInterval(tick); clearInterval(txt); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attempt]);

    if (failed) {
        return (
            <div className="fixed inset-0 bg-black max-w-[480px] mx-auto flex flex-col items-center justify-center px-8 text-center" data-testid="loading-retry-screen">
                <h1 className="font-display font-black uppercase text-white" style={{ fontSize: 32, lineHeight: 1.05 }}>
                    Petit souci, on relance ton analyse
                </h1>
                <p className="font-body" style={{ marginTop: 12, fontSize: 15, color: "var(--text-secondary)" }}>
                    Ta photo est bien enregistrée. On réessaie avec la même photo.
                </p>
                <button
                    data-testid="btn-retry-analysis"
                    onClick={() => setAttempt((a) => a + 1)}
                    className="tap-feedback mt-8 flex items-center gap-2 rounded-full bg-revred text-white font-display font-black uppercase"
                    style={{ height: 56, paddingLeft: 28, paddingRight: 28 }}
                >
                    <RotateCcw size={18} /> Réessayer
                </button>
            </div>
        );
    }

    const offset = C * (1 - pct / 100);

    return (
        <div className="fixed inset-0 bg-black max-w-[480px] mx-auto flex flex-col items-center justify-center" data-testid="loading-screen">
            <div className="relative" style={{ width: 220, height: 220 }}>
                <svg width="220" height="220" viewBox="0 0 220 220" style={{ transform: "rotate(-90deg)" }}>
                    <circle cx="110" cy="110" r={R} fill="none" stroke="var(--border)" strokeWidth="1" />
                    <circle
                        cx="110" cy="110" r={R} fill="none"
                        stroke="var(--red)" strokeWidth="1"
                        strokeDasharray={C} strokeDashoffset={offset} strokeLinecap="round"
                        style={{ transition: "stroke-dashoffset 180ms linear" }}
                    />
                </svg>
                <span
                    data-testid="loading-pct"
                    className="absolute inset-0 flex items-center justify-center font-display"
                    style={{ fontSize: "72px", fontWeight: 800, color: "#FFFFFF" }}
                >
                    {pct}
                </span>
            </div>
            <p data-testid="loading-text" className="font-body text-center" style={{ marginTop: 32, fontSize: "14px", color: "var(--text-secondary)", minHeight: 20, paddingLeft: 24, paddingRight: 24 }}>
                {lines[lineIdx]}
            </p>
            {pct >= 90 && (
                <p data-testid="loading-hint" className="font-body text-center" style={{ marginTop: 10, fontSize: "13px", color: "var(--text-secondary)", paddingLeft: 24, paddingRight: 24 }}>
                    Analyse en cours, ne quitte pas cette page.
                </p>
            )}
        </div>
    );
};

export default Loading;
